import React, { useState } from 'react'
import { DatePicker, Drawer, TimePicker } from 'antd'
import moment from 'moment-timezone'

export const MAX_WINDOWS = 4

let windowKeySeq = 0
export const newWindow = () => ({ key: 'w' + windowKeySeq++, date: null, times: null })

const windowLabel = w => {
  if (!w.date || !w.times || !w.times[0] || !w.times[1]) { return '' }
  return w.times[0].format('h:mm A') + ' – ' + w.times[1].format('h:mm A')
}

/*
Availability windows for the inspection-scheduling page, styled after the
canonical recipient-flow mockup: compact tinted window cards with edit/delete,
a dashed add button, and a bottom-sheet editor (antd Drawer) for add/edit.
Times are picked in the property's local timezone; the parent composes them
into ISO strings on submit.
*/
const AvailabilityWindows = ({ windows, onChange, error }) => {
  const [sheet, setSheet] = useState(null) // null | { key|null, date, times }

  const openAdd = () => setSheet({ key: null, date: null, times: null })
  const openEdit = w => setSheet({ key: w.key, date: w.date, times: w.times })
  const closeSheet = () => setSheet(null)

  const removeWindow = key => onChange(windows.filter(w => w.key !== key))

  const canSave = sheet && sheet.date && sheet.times && sheet.times[0] && sheet.times[1] &&
    sheet.times[1].isAfter(sheet.times[0])

  const saveSheet = () => {
    if (!canSave) { return }
    if (sheet.key) {
      onChange(windows.map(w => (w.key === sheet.key ? { ...w, date: sheet.date, times: sheet.times } : w)))
    } else {
      onChange([...windows, { ...newWindow(), date: sheet.date, times: sheet.times }])
    }
    closeSheet()
  }

  const disabledDate = current => current && current < moment().startOf('day')

  return (
    <div data-testid='availability-windows'>
      {windows.map((w, index) => (
        <div className='isp-window-card' key={w.key} data-testid={'availability-window-' + index}>
          <div className='isp-window-text'>
            <span className='date'>{w.date ? w.date.format('ddd, MMM D') : ''}</span>
            <span className='sep'>&middot;</span>
            {windowLabel(w)}
          </div>
          <div className='isp-window-actions'>
            <button type='button' className='isp-icon-btn' aria-label='Edit this time window' onClick={() => openEdit(w)}>
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M12 20h9' /><path d='M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z' />
              </svg>
            </button>
            <button
              type='button'
              className='isp-icon-btn delete'
              aria-label='Remove this time window'
              onClick={() => removeWindow(w.key)}
            >
              <svg viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <path d='M3 6h18' /><path d='M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2' /><path d='M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6' />
              </svg>
            </button>
          </div>
        </div>
      ))}
      {error && <div className='isp-error' data-testid='availability-error'>{error}</div>}
      {windows.length < MAX_WINDOWS && (
        <button type='button' className='isp-add-window' data-testid='availability-add' onClick={openAdd}>
          + Add availability
        </button>
      )}

      <Drawer
        placement='bottom'
        closable={false}
        onClose={closeSheet}
        visible={!!sheet}
        height={380}
        className='isp-sheet'
        destroyOnClose
      >
        {sheet && (
          <div className='isp-sheet-body'>
            <div className='isp-sheet-handle' />
            <div className='isp-sheet-header'>
              <button className='isp-sheet-cancel' onClick={closeSheet}>Cancel</button>
              <div className='isp-sheet-title'>{sheet.key ? 'Edit availability' : 'Add availability'}</div>
              <span className='isp-sheet-spacer' />
            </div>
            <div className='isp-sheet-field'>
              <div className='isp-sheet-label'>Date</div>
              <DatePicker
                className='isp-sheet-input'
                placeholder='Select a date'
                format='ddd, MMM D'
                inputReadOnly
                disabledDate={disabledDate}
                value={sheet.date}
                onChange={date => setSheet({ ...sheet, date })}
              />
            </div>
            <div className='isp-sheet-field'>
              <div className='isp-sheet-label'>Time window</div>
              <TimePicker.RangePicker
                className='isp-sheet-input'
                placeholder={['From', 'To']}
                format='h:mm A'
                use12Hours
                minuteStep={15}
                inputReadOnly
                order
                value={sheet.times}
                onChange={times => setSheet({ ...sheet, times })}
              />
            </div>
            <button className='isp-cta isp-sheet-save' disabled={!canSave} onClick={saveSheet}>
              Save
            </button>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default AvailabilityWindows
